// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getGraphQLActionErrorReason, parseActionResponseBody } from "@/services/graphqlActionError";
import { getPasswordPolicyMessage } from "./editPasswordError";
export var USER_PROFILE_VALIDATION_ERROR_CODE = "UserProfileValidation";
// Keycloak's own constraint keys, mapped to what the admin is told. Anything
// Keycloak may add later falls back to the generic message rather than
// surfacing a raw key.
var CONSTRAINT_MESSAGES = {
    "error-invalid-length": "invalidLength",
    "error-invalid-length-too-short": "tooShort",
    "error-invalid-length-too-long": "tooLong",
    "error-user-attribute-required": "required",
    "error-invalid-email": "invalidEmail",
    "error-pattern-no-match": "invalidFormat",
    "error-invalid-value": "invalid",
};
var readValidation = function (value) {
    var entry = value;
    // Only an entry naming the attribute it refused is one of these, the same
    // way Harvest decides it.
    if (typeof entry !== "object" || entry === null || typeof entry.field !== "string") {
        return undefined;
    }
    return {
        field: entry.field,
        error: typeof entry.error === "string" ? entry.error : undefined,
        params: Array.isArray(entry.params) ? entry.params : [],
    };
};
var readValidations = function (value) {
    var extensions = value;
    if ((extensions === null || extensions === void 0 ? void 0 : extensions.code) !== USER_PROFILE_VALIDATION_ERROR_CODE) {
        return undefined;
    }
    var reported = (Array.isArray(extensions.user_profile_errors) ? extensions.user_profile_errors : [])
        .map(readValidation)
        .filter(function (entry) { return entry !== undefined; });
    if (reported.length === 0) {
        return undefined;
    }
    var total = extensions.user_profile_errors_total;
    return {
        reported: reported,
        total: typeof total === "number" && total > reported.length ? total : reported.length,
    };
};
/**
 * The user profile constraint Keycloak refused the write against, when it named
 * one. Harvest forwards the attribute and the constraint's arguments, so the
 * admin can be told which field to correct rather than that something failed.
 */
var getUserProfileValidations = function (error) {
    var _a, _b, _c;
    var actionError = error;
    var graphQLErrors = Array.isArray(actionError === null || actionError === void 0 ? void 0 : actionError.graphQLErrors) ? actionError.graphQLErrors : [];
    for (var _i = 0, graphQLErrors_1 = graphQLErrors; _i < graphQLErrors_1.length; _i++) {
        var graphQLError = graphQLErrors_1[_i];
        var direct = readValidations(graphQLError.extensions);
        if (direct) {
            return direct;
        }
        var responseBody = parseActionResponseBody((_c = (_b = (_a = graphQLError.extensions) === null || _a === void 0 ? void 0 : _a.internal) === null || _b === void 0 ? void 0 : _b.response) === null || _c === void 0 ? void 0 : _c.body);
        var fromBody = readValidations(responseBody === null || responseBody === void 0 ? void 0 : responseBody.extensions);
        if (fromBody) {
            return fromBody;
        }
    }
    return undefined;
};
// Keycloak repeats the attribute name as the constraint's first argument, and
// the message already names the field.
var constraintArguments = function (validation) {
    return validation.params[0] === validation.field ? validation.params.slice(1) : validation.params;
};
var interpolation = function (messageKey, args) {
    switch (messageKey) {
        case "invalidLength":
            return { min: args[0], max: args[1] };
        case "tooShort":
            return { min: args[0] };
        case "tooLong":
            return { max: args[0] };
        default:
            return {};
    }
};
var describeValidation = function (validation, t, resolveFieldLabel) {
    var _a, _b;
    var rawField = (_a = validation.field) !== null && _a !== void 0 ? _a : "";
    var field = resolveFieldLabel && rawField ? resolveFieldLabel(rawField) : rawField;
    var messageKey = CONSTRAINT_MESSAGES[(_b = validation.error) !== null && _b !== void 0 ? _b : ""];
    // A constraint with no wording of its own still names its attribute, and
    // carries what Keycloak called it rather than saying only that something
    // was invalid.
    if (!messageKey) {
        return validation.error
            ? t("usersAndRolesScreen.voters.errors.attribute.invalidNamed", {
                field: field,
                constraint: validation.error,
            })
            : t("usersAndRolesScreen.voters.errors.attribute.invalid", { field: field });
    }
    return t("usersAndRolesScreen.voters.errors.attribute.".concat(messageKey), __assign({ field: field }, interpolation(messageKey, constraintArguments(validation))));
};
var getUserProfileValidationMessage = function (error, t, resolveFieldLabel) {
    var validations = getUserProfileValidations(error);
    if (!validations) {
        return undefined;
    }
    var described = validations.reported
        .map(function (validation) { return describeValidation(validation, t, resolveFieldLabel); })
        .join("; ");
    var unreported = validations.total - validations.reported.length;
    // Keycloak refuses every attribute at once, so a mis-mapped import can
    // produce more than a message can carry. Say so rather than let the rest
    // pass unmentioned.
    return unreported > 0
        ? "".concat(described, "; ").concat(t("usersAndRolesScreen.voters.errors.attribute.andMore", {
            count: unreported,
        }))
        : described;
};
/**
 * Message shown when saving a voter fails: the reason the backend gave when
 * there is one, and the bare failure message when there is not.
 */
export var getSaveUserErrorMessage = function (error, messageKey, reasonKey, t, resolveFieldLabel) {
    var _a, _b;
    // The voter editor submits passwords through the same `edit_user` action as
    // EditPassword, so a rejected password reads the same way in both places
    // rather than falling back to Harvest's untranslated text. A refused
    // attribute is likewise stated in the admin's language, naming the field.
    var reason = (_b = (_a = getPasswordPolicyMessage(error, t)) !== null && _a !== void 0 ? _a : getUserProfileValidationMessage(error, t, resolveFieldLabel)) !== null && _b !== void 0 ? _b : getGraphQLActionErrorReason(error);
    return reason ? t(reasonKey, { reason: reason }) : t(messageKey);
};
